/**
 * ScoreboardsPage — Apr 30 2026
 *
 * Standalone "Scoreboards" page accessible from the team menu.
 * Lists every game with its final score, plus aggregate W/L/T
 * + goals for/against. Foundation for season recap and AI
 * analysis (the underlying score_events table also captures
 * the per-event timeline that AI can mine later).
 *
 * Hidden from the menu when the team has scoreboard_enabled =
 * false (the layouts filter the menu item out). The page itself
 * also renders a polite "feature is off" notice if a stale
 * session lands here via a bookmark.
 *
 * Per user spec: "keep track of the games have like a game one
 * a game to keep the history save it to the database because
 * later on we can use it for analysis we can track what each
 * team how many goals they've scored". So:
 *   • Game numbering: shown as "Game N" where N is the
 *     chronological position within the season (or all-time
 *     if no season is set). Helps coaches reference games.
 *   • Goals for/against: aggregated at the top.
 *   • W-L-T: home > away counts as a win (per the data model
 *     comment in routes/teams.js — we treat home as "us").
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { Trophy, ChevronLeft, ChevronRight as ChevR } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import api from '../services/api.js';
import { useWakeLock } from '../hooks/useWakeLock.js';

export default function ScoreboardsPage() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { teams, currentSeasonId = null } = useOutletContext() || {}; // v2.5.189
  const team = (teams || []).find(t => t.id === teamId);
  const enabled = team?.settings?.scoreboard_enabled !== false;

  // v2.5.186 — keep the screen awake on this page. Coaches set the phone
  // on the bench to run the live scoreboard during a game and the iPhone
  // kept auto-locking. No-ops on unsupported browsers.
  useWakeLock(enabled);

  const [games, setGames] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // May 4 2026 — coach asked for explicit Past/Upcoming tabs instead
  // of one merged list. Default to upcoming since "what's next" is the
  // typical reason to land on this page; coach reviewing history flips
  // to Past. "Today" games (live scoreboard) appear in Upcoming for
  // easy access to the +/- buttons.
  const [tab, setTab] = useState('upcoming');
  // v2.5.107 — coach feedback: "can you group the games by day (past
  // names and current games), so that there's not multiple days with
  // mini games. Actually, have an option to toggle it by day or show
  // all. Have it group by day as a default."
  // Common case: many teams have 2 games on the same day (early +
  // late ice slots, or doubleheaders). The flat list buried the
  // shared date in each row. Grouped view shows one date header per
  // day with all that day's games stacked under it. Coach can flip
  // to "Show all" if they want the flat one-row-per-game view back.
  // Preference persists per-browser via localStorage so the choice
  // sticks across visits without a settings round-trip.
  // 2026-09-17 (per Gilles) — the "By day / Show all" toggle is gone:
  // grouping by day is the only view now. `groupBy` stays as a constant
  // so the render paths below don't move; the flat path is dead code.
  const groupBy = 'day';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { limit: 200 };
    if (currentSeasonId) params.season_id = currentSeasonId; // v2.5.189
    api.get(`/teams/${teamId}/scoreboards`, { params })
      .then(({ data }) => {
        if (cancelled) return;
        setGames(data.games || []);
        setSummary(data.summary || null);
        setError(null);
      })
      .catch(err => {
        if (!cancelled) setError(err.response?.data?.error || 'Failed to load scoreboards');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [teamId, currentSeasonId]);

  // May 4 2026 — coach feedback: "these games are at the bottom, put
  // them in the correct sorting order with the most recent game at
  // the top. Add a tab that separates Past games vs Upcoming."
  // Was: today → upcoming asc → past desc all in one list, which mixed
  // the past games at the bottom of the page.
  // Now: separate Past vs Upcoming buckets, exposed as `numbered` for
  // the active tab. Most-recent-first ordering in BOTH tabs (so the
  // freshly-completed game and the next scheduled game both sit at
  // the top of their respective tabs).
  const { upcomingGames, pastGames, totalGames } = useMemo(() => {
    // Assign chronological game numbers based on natural API order
    // (newest-first). Game N is the Nth game on record — same numbering
    // regardless of which tab the user is on.
    const total = games.length;
    const withNumbers = games.map((g, i) => ({ ...g, gameNumber: total - i }));

    const todayISO = (() => {
      const d = new Date();
      return d.getFullYear() + '-' +
        String(d.getMonth() + 1).padStart(2, '0') + '-' +
        String(d.getDate()).padStart(2, '0');
    })();

    const upcoming = [];
    const past = [];
    for (const g of withNumbers) {
      const date = (g.game_date || '').slice(0, 10);
      if (date === todayISO) {
        upcoming.push({ ...g, isToday: true });   // today shows in Upcoming
      } else if (date > todayISO) {
        upcoming.push(g);
      } else {
        past.push(g);
      }
    }
    // Upcoming: today first, then soonest-future-first (asc).
    upcoming.sort((a, b) => {
      if (a.isToday && !b.isToday) return -1;
      if (!a.isToday && b.isToday) return 1;
      return (a.game_date || '').localeCompare(b.game_date || '');
    });
    // Past: most recent first (desc) — coach review pattern is
    // "what just happened" not "scroll back to last August."
    past.sort((a, b) => (b.game_date || '').localeCompare(a.game_date || ''));
    return { upcomingGames: upcoming, pastGames: past, totalGames: total };
  }, [games]);

  const numbered = tab === 'past' ? pastGames : upcomingGames;

  // v2.5.107 — compute date-grouped buckets from `numbered`. Result
  // shape: [{ dateISO, label, games: [...] }, ...] preserving the
  // outer game ordering (past=desc, upcoming=today-first-then-asc).
  // Date label is "EEE, MMM d, yyyy" (e.g. "Sun, May 10, 2026") to
  // match the look of the existing per-row date. Computed even when
  // groupBy='none' to keep the hook order stable; render decides
  // which to display.
  const groupedNumbered = useMemo(() => {
    const buckets = [];
    let cur = null;
    for (const g of numbered) {
      const iso = (g.game_date || '').slice(0, 10);
      if (!cur || cur.dateISO !== iso) {
        let label = iso;
        try { label = format(parseISO(iso), 'EEE, MMM d, yyyy'); } catch {}
        cur = { dateISO: iso, label, games: [] };
        buckets.push(cur);
      }
      cur.games.push(g);
    }
    return buckets;
  }, [numbered]);

  if (!enabled) {
    return (
      <div className="animate-fade-in">
        <div className="card mb-4 text-center" style={{ padding: 32 }}>
          <Trophy size={28} style={{ margin: '0 auto 12px', color: 'var(--text-3)' }} />
          <h2 className="font-bold text-lg mb-2">Scoreboards turned off</h2>
          <p className="text-secondary text-sm">
            A coach has disabled the scoreboard feature for this team.
            Enable it in <strong>Team Settings → Scoreboard</strong> to
            track scores and view game history here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <Trophy size={20} style={{ color: 'var(--ice)' }} />
        <h1 className="section-title text-2xl">Scoreboards</h1>
      </div>

      {/* Summary */}
      {summary && summary.games_with_scores > 0 && (
        <div className="card mb-4">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, textAlign: 'center' }}>
            <Stat label="W-L-T" value={`${summary.wins}-${summary.losses}-${summary.ties}`} accent="var(--ice)" />
            <Stat label="Goals For" value={summary.goals_for} accent="var(--goal)" />
            <Stat label="Goals Against" value={summary.goals_against} accent="var(--penalty)" />
          </div>
          <div className="text-secondary text-xs text-center mt-3">
            Across {summary.games_with_scores} game{summary.games_with_scores !== 1 ? 's' : ''} with recorded scores.
            Goal differential: {summary.goals_for - summary.goals_against >= 0 ? '+' : ''}
            {summary.goals_for - summary.goals_against}.
          </div>
        </div>
      )}

      {/* Loading / error / empty / list */}
      {loading && <div className="text-secondary text-center p-4">Loading…</div>}
      {error && <div className="card text-center p-4" style={{ color: '#ff6b6b' }}>{error}</div>}

      {/* May 4 2026 — Past / Upcoming tabs. Was a single mixed list
          with today → upcoming asc → past desc all together which
          buried the past games beneath every future fixture and made
          "what was the score last week" require deep scrolling.
          Now: explicit tabs. Today's game (if any) lives in Upcoming
          so the live scoreboard is one tap away. Counts on each tab
          so a coach can scan the season at a glance. */}
      {!loading && !error && totalGames > 0 && (
        <div className="flex items-center gap-2 mb-3" role="tablist">
          {[
            { id: 'upcoming', label: 'Upcoming', count: upcomingGames.length },
            { id: 'past',     label: 'Past',     count: pastGames.length },
          ].map(t => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t.id)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', borderRadius: 10,
                  background: active ? 'var(--ice-a10)' : 'var(--surface-2)',
                  border: `1px solid ${active ? 'var(--ice-a30)' : 'var(--border)'}`,
                  color: active ? 'var(--ice)' : 'var(--text-2)',
                  fontFamily: 'var(--font-display)',
                  fontSize: 12, fontWeight: 700, letterSpacing: '0.04em',
                  cursor: 'pointer',
                  transition: 'all 0.12s',
                }}
              >
                {t.label}
                <span style={{
                  fontSize: 10, fontWeight: 800,
                  padding: '1px 6px', borderRadius: 999,
                  background: active ? 'rgba(0,212,255,0.25)' : 'var(--surface-3)',
                  color: active ? 'var(--ice)' : 'var(--text-3)',
                }}>{t.count}</span>
              </button>
            );
          })}
        </div>
      )}

      {!loading && !error && numbered.length === 0 && totalGames === 0 && (
        <div className="card text-center" style={{ padding: 28 }}>
          <p className="text-secondary text-sm">
            No games yet. Schedule a game and track the score on its detail page —
            it'll show up here for season recap.
          </p>
        </div>
      )}

      {!loading && !error && numbered.length === 0 && totalGames > 0 && (
        <div className="card text-center" style={{ padding: 24 }}>
          <p className="text-secondary text-sm">
            {tab === 'upcoming'
              ? 'No upcoming games scheduled. Check the Past tab for game history.'
              : 'No past games yet. Recorded games will appear here after they\u2019re completed.'}
          </p>
        </div>
      )}

      {/* v2.5.107 — group-by-day toggle. Coach feedback: "can you
          group the games by day (past names and current games), so
          that there's not multiple days with mini games. Actually,
          have an option to toggle it by day or show all. Have it
          group by day as a default." Default is 'day' so the typical
          case (multiple games on the same date) reads cleanly with
          one shared header. Persisted in localStorage. */}
      {/* 2026-09-17 — "Group: By day / Show all" toggle removed (per
          Gilles); always grouped by day. */}

      {/* Game list. v2.5.107 — extracted row render into renderRow so
          both the flat path (groupBy='none') and the grouped path
          (groupBy='day' with date headers) share the same row JSX
          without duplication. `isPast` is a constant per tab so the
          row helper closes over it cleanly. */}
      {!loading && numbered.length > 0 && (() => {
        const isPast = tab === 'past';
        const renderRow = (g, idx, total) => {
          const hasScore = g.home_score != null && g.away_score != null;
          // v2.5.186 — compute W/L from the side THIS player was on, using
          // my_team_assignment from the backend. Previously this always
          // treated home as "us" (home>away = W), so in balanced pickup
          // games — where a player can be on home OR away game to game —
          // the badge was wrong and every card trended the same colour.
          // If the player wasn't on the roster (assignment null) we show
          // the neutral score with no W/L badge: it wasn't their result.
          const myScore  = g.my_team_assignment === 'away' ? g.away_score : g.home_score;
          const oppScore = g.my_team_assignment === 'away' ? g.home_score : g.away_score;
          const result = (hasScore && (g.my_team_assignment === 'home' || g.my_team_assignment === 'away'))
            ? (myScore > oppScore ? 'W' : myScore < oppScore ? 'L' : 'T')
            : null;
          const resultColor = result === 'W' ? 'var(--goal)' : result === 'L' ? 'var(--penalty)' : 'var(--text-2)';
          const targetUrl = `/team/${teamId}/games/${g.game_id}/scoreboard`;
          // 2026-09-17 (per Gilles) — a player who was OUT (or flagged a
          // no-show) that night still sees the scores, but the row says so
          // instead of pretending it was their result. Only for past rows
          // with no roster side (if they were on a roster they played).
          const wasOut = isPast && !result && (g.my_no_show === true || g.my_status === 'out');
          return (
            <div
              key={g.game_id}
              onClick={() => navigate(targetUrl)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate(targetUrl); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 14px',
                borderBottom: idx < total - 1 ? '1px solid var(--border)' : 'none',
                background: g.isToday ? 'rgba(0,212,255,0.06)' : 'transparent',
                borderLeft: g.isToday ? '3px solid var(--ice)' : '3px solid transparent',
                cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
                transition: 'background 0.12s',
              }}
              onMouseEnter={(e) => {
                if (!g.isToday) e.currentTarget.style.background = 'rgba(0,212,255,0.04)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = g.isToday ? 'rgba(0,212,255,0.06)' : 'transparent';
              }}
            >
              {/* Game # badge */}
              <div style={{
                width: 38, height: 38, borderRadius: 8, flexShrink: 0,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(0,212,255,0.08)',
                border: '1px solid rgba(0,212,255,0.18)',
                fontFamily: 'var(--font-display)', color: 'var(--ice)',
              }}>
                <div style={{ fontSize: 8, letterSpacing: '0.1em', opacity: 0.7 }}>GAME</div>
                <div style={{ fontSize: 13, fontWeight: 800, lineHeight: 1 }}>{g.gameNumber}</div>
              </div>

              {/* Date + opponent. v2.5.107 — when in grouped mode, the
                  date header above already shows the date; hide the
                  per-row date to avoid redundancy. Show arena alone. */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="font-semibold text-sm" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {g.opponent || 'Game'}
                  </span>
                  {g.isToday && (
                    <span style={{
                      fontSize: 9, fontWeight: 800, letterSpacing: '0.1em',
                      padding: '2px 6px', borderRadius: 4,
                      background: 'var(--ice)', color: '#000',
                      fontFamily: 'var(--font-display)',
                      flexShrink: 0,
                    }}>TONIGHT</span>
                  )}
                </div>
                <div className="text-secondary text-xs" style={{ marginTop: 2 }}>
                  {groupBy === 'day'
                    ? (g.arena_name || (g.game_time ? format(parseISO(g.game_date + 'T' + (g.game_time.length === 5 ? g.game_time : '00:00') + ':00'), 'h:mm a') : ''))
                    : (<>{format(parseISO(g.game_date), 'EEE, MMM d, yyyy')}{g.arena_name && ` · ${g.arena_name}`}</>)
                  }
                </div>
              </div>

              {/* Score / W-L badge */}
              <div style={{ textAlign: 'right', minWidth: 70 }}>
                {hasScore ? (
                  <>
                    <div style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: 18, fontWeight: 800,
                      color: 'var(--text)',
                      fontVariantNumeric: 'tabular-nums',
                    }}>
                      {/* v2.5.186 — show the player's own side first
                          (yours–theirs) when we know which team they were
                          on, so the score reads naturally next to the W/L.
                          Falls back to home–away when they didn't play. */}
                      {(g.my_team_assignment === 'home' || g.my_team_assignment === 'away')
                        ? `${myScore}–${oppScore}`
                        : `${g.home_score}–${g.away_score}`}
                    </div>
                    {result ? (
                      <div style={{
                        fontSize: 10, fontWeight: 700, letterSpacing: '0.1em',
                        color: resultColor, marginTop: 2,
                      }}>{result}</div>
                    ) : wasOut ? (
                      <div style={{
                        fontSize: 9, fontWeight: 800, letterSpacing: '0.08em', marginTop: 3,
                        color: g.my_no_show === true ? 'var(--puck)' : 'var(--penalty)',
                        fontFamily: 'var(--font-display)', textTransform: 'uppercase',
                      }} title={g.my_no_show === true ? 'You were flagged as a no-show for this game' : 'You were checked out for this game'}>
                        {g.my_no_show === true ? '👻 No-show' : '❌ You were out'}
                      </div>
                    ) : null}
                  </>
                ) : isPast ? (
                  <div className="text-secondary text-xs">—</div>
                ) : (
                  <div style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                    color: 'var(--ice)', fontFamily: 'var(--font-display)',
                  }}>{g.isToday ? 'LIVE →' : 'TRACK →'}</div>
                )}
              </div>

              <ChevR size={16} style={{ color: 'var(--text-3)', flexShrink: 0 }} />
            </div>
          );
        };

        if (groupBy === 'day') {
          return (
            <div>
              {groupedNumbered.map((bucket) => (
                <div key={bucket.dateISO} className="mb-3">
                  {/* Date header — sticky-ish visual treatment so the
                      day a coach is scrolling through is always clear. */}
                  <div style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 11, fontWeight: 800, letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: 'var(--text-2)',
                    padding: '6px 4px 6px 4px',
                    marginBottom: 4,
                  }}>
                    {bucket.label}
                    <span style={{
                      marginLeft: 8,
                      padding: '1px 6px', borderRadius: 999,
                      background: 'var(--surface-3)', color: 'var(--text-3)',
                      fontSize: 9, fontWeight: 700,
                    }}>{bucket.games.length}</span>
                  </div>
                  <div className="card" style={{ padding: 0 }}>
                    {bucket.games.map((g, i) => renderRow(g, i, bucket.games.length))}
                  </div>
                </div>
              ))}
            </div>
          );
        }

        // Flat (Show all) — original look.
        return (
          <div className="card" style={{ padding: 0 }}>
            {numbered.map((g, idx) => renderRow(g, idx, numbered.length))}
          </div>
        );
      })()}

      {/* Future-AI hint */}
      {!loading && numbered.length > 0 && (
        <p className="text-secondary text-xs mt-3 px-1" style={{ lineHeight: 1.5 }}>
          💡 Every score change is recorded in the team's history.
          We'll use this for season analysis and trend detection in
          a future update.
        </p>
      )}
    </div>
  );
}

function BackBar({ onBack }) {
  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 10,
      background: 'var(--bg)', margin: '-12px -14px 12px',
      padding: '8px 14px 6px',
    }}>
      <button onClick={onBack} className="btn-ghost -ml-1"
        style={{ padding: '8px 12px', fontSize: 12, minHeight: 38, borderRadius: 10 }}>
        <ChevronLeft size={15} /> Back
      </button>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div>
      <div style={{
        fontFamily: 'var(--font-display)',
        fontSize: 22, fontWeight: 900, color: accent,
        fontVariantNumeric: 'tabular-nums',
      }}>{value}</div>
      <div style={{
        fontFamily: 'var(--font-display)',
        fontSize: 9, letterSpacing: '0.18em',
        textTransform: 'uppercase', color: 'var(--text-2)',
        marginTop: 2,
      }}>{label}</div>
    </div>
  );
}
